import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import omit from 'lodash/omit';
import toast from 'react-hot-toast';

import * as componentLibrary from '../../component-lib';
import { PAGE_ROOT } from '../../constants';
import { doneLoading, startLoading } from '../../loading/actions';
import { positionInParent } from '../../outline-manipulation';
import { updateMarketplaceOffers } from '../../update-marketplace-offers';
import * as constants from './action-types';

export const genKey = () => Date.now() + Math.floor(Math.random() * 1_000_000);

// makeId must return a unique ID
// we want it to be an integer for testing purposes
export const makeId = (componentDocument) => {
  const randomInt = genKey();
  return `${randomInt}-${componentDocument.type}`;
};

/**
 * Recursively regenerates all `$unique_key` values inside an object or array.
 *
 * @param {any} value - The object, array, or primitive to process.
 * @returns {any} - A new structure with updated `$unique_key` values.
 */
export const regenerateUniqueKeys = (value) => {
  if (value === null || typeof value !== 'object') {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map(regenerateUniqueKeys);
  }

  const updatedObject = {};
  Object.entries(value).forEach(([key, val]) => {
    if (key === '$unique_key') {
      updatedObject[key] = genKey();
      return;
    }
    updatedObject[key] = regenerateUniqueKeys(val);
  });

  return updatedObject;
};

export const builderLoaded = () => ({ type: constants.BUILDER_LOADED });

export const addComponent = (component, order) => ({
  type: constants.COMPONENT_ADDED,
  component,
  order,
});

export const addRepeatable = (path, repeatable = {}) => ({
  type: constants.ADD_REPEATABLE,
  path,
  repeatable,
});
export const moveRepeatable = (path, fromIndex, toIndex) => ({
  type: constants.MOVE_REPEATABLE,
  path,
  fromIndex,
  toIndex,
});
export const deleteRepeatable = (path, index) => ({
  type: constants.DELETE_REPEATABLE,
  path,
  index,
});

export const duplicateRepeatable = (path, index) => ({
  type: constants.DUPLICATE_REPEATABLE,
  path,
  index,
});

export const duplicateGridItemComponent = (path, index) => ({
  type: constants.DUPLICATE_GRID_ITEM_COMPONENT,
  path,
  index,
});

export const duplicateDynamicGridObject = (path) => ({
  type: constants.DUPLICATE_DYNAMIC_GRID_OBJECT,
  path,
});

export const addComponentToDynamicGridObject = (path, newComponent) => ({
  type: constants.ADD_COMPONENT_TO_DYNAMIC_GRID,
  path,
  newComponent,
});

export const addContainerToDynamicGridObject = (path, newContainer) => ({
  type: constants.ADD_CONTAINER_TO_DYNAMIC_GRID,
  path,
  newContainer,
});

export const deleteDynamicGridElement = (path) => ({
  type: constants.DELETE_DYNAMIC_GRID_ELEMENT,
  path,
});

export const moveDynamicGridObject = (path, fromIndex, toIndex) => ({
  type: constants.MOVE_DYNAMIC_GRID_OBJECT,
  path,
  fromIndex,
  toIndex,
});

export const updateDynamicGridElement = (path, element) => ({
  type: constants.UPDATE_DYNAMIC_GRID_ELEMENT,
  path,
  element,
});

export const receiveOutline = (outline) => ({
  type: constants.OUTLINE_RECEIVED,
  outline,
});

export const closeEditor = {
  type: constants.EDITOR_CLOSE,
};

export const toggleEditor = () => ({
  type: constants.TOGGLE_EDITOR,
});

export const openEditor = (abTesting) => ({
  type: constants.EDITOR_OPEN,
  abTesting,
});

export const selectTab = (tabIndex) => ({
  type: constants.EDITOR_TAB_SELECT,
  tab: tabIndex,
});

export const updateEditorWidth = (width) => ({
  type: constants.EDITOR_UPDATE_WIDTH,
  width,
});

export const updateEditorHeight = (height) => ({
  type: constants.EDITOR_UPDATE_HEIGHT,
  height,
});

export const updateEditorDock = (dock) => ({
  type: constants.EDITOR_UPDATE_DOCK,
  dock,
});

export const selectComponent = (id) => ({
  type: constants.COMPONENT_SELECTED,
  id,
});
export const setComponentPath = (path) => ({
  type: constants.SET_COMPONENT_PATH,
  path,
});

export const setComponentType = (componentType) => ({
  type: constants.SET_COMPONENT_TYPE,
  componentType,
});
export const updateComponent = (component) => ({
  type: constants.COMPONENT_UPDATED,
  component,
});

export const deleteComponent = (id) => ({
  type: constants.COMPONENT_DELETED,
  id,
});

export const toggleBadge = (componentId) => ({
  type: constants.TOGGLE_BADGE,
  componentId,
});

export const showAllViewPorts = (value) => ({
  type: constants.EDITOR_SHOW_ALL_VIEW_PORTS,
  payload: value,
});

export const changeMobileExperienceView = (value) => ({
  type: constants.EDITOR_CHANGE_MOBILE_EXPERIENCE_VIEW,
  payload: value,
});

export const toggleAllViewPorts = () => ({
  type: constants.EDITOR_TOGGLE_ALL_VIEW_PORTS,
});

export const closeAllBadges = {
  type: constants.CLOSE_ALL_BADGES,
};

/**
 * Set the version of the LPC
 * @param {string} version version in string defaults to ""
 */
export const setBuilderVersion = (version = '') => ({
  type: constants.SET_BUILDER_VERSION,
  version,
});

// some components, like the QuerySection, display selectively depending on what query params are set
// this flag determines whether or not we should watch query params for these components when we're in the builder
// so when this flag is false, it indicates that all components should display in the builder for editing purposes
export const watchQueryParamsInBuilder = (watchQueryParams) => ({
  type: constants.SHOULD_WATCH_QUERY_PARAMS,
  watchQueryParams,
});

// for components that selectively render, we may want to override their default state of rendering in the builder and
// force them to render depending on user input
export const alwaysRenderInBuilder = (id) => ({
  type: constants.ALWAYS_RENDER_IN_BUILDER,
  id,
});

/**
 * Copies the given component and inserts the copy directly below the original. Recurses on all children,
 * copying them as well.
 *
 * @param {String} idToCopy - the id of the component that you want to copy
 * @param {String} parentId (optional) - the ID of the parent component for the new copy. defaults to existing parentId
 */
export const copyComponent =
  (idToCopy, parentId = undefined) =>
  (dispatch, getState) => {
    const state = getState();
    const flatOutline = get(state, 'builder.flatOutline');
    const componentToCopy = get(flatOutline, idToCopy, {});
    const type = get(componentToCopy, 'type', null);
    if (isEmpty(componentToCopy) || type === null) return;

    const newId = makeId(componentToCopy);
    // we don't want to pass down children, we copy them explicitly below
    let copiedProps = omit(componentToCopy, 'children');

    // Regenerate all $unique_key properties in the component props
    copiedProps = regenerateUniqueKeys(copiedProps);

    if (copiedProps.props) {
      copiedProps.props.isCopy = true;
    }
    const newComponent = {
      ...copiedProps,
      id: newId,
      parentId: parentId || get(componentToCopy, 'parentId', PAGE_ROOT),
    };

    // Add the component
    dispatch(
      addComponent(
        newComponent,
        1 + positionInParent(idToCopy, componentToCopy.parentId, flatOutline)
      )
    );

    // recursively copy all children of this component
    if (componentToCopy.children) {
      componentToCopy.children.forEach((childComponentId) =>
        dispatch(copyComponent(childComponentId, newId))
      );
    }
  };

export const createTemplate =
  (idToCopy, newOfferId) => (dispatch, getState) => {
    const state = getState();
    const flatOutline = get(state, 'builder.flatOutline');
    const componentToCopy = get(flatOutline, idToCopy, {});
    const type = get(componentToCopy, 'type', null);
    if (isEmpty(componentToCopy) || type === null) return;

    const newId = makeId(componentToCopy);
    // we don't want to pass down children, we copy them explicitly below
    let copiedProps = omit(componentToCopy, 'children');
    copiedProps = regenerateUniqueKeys(copiedProps);

    if (copiedProps.props) {
      copiedProps.props.isCopy = true;
    }

    let newComponent = {
      ...copiedProps,
      props: {
        ...copiedProps.props,
        options: {
          ...copiedProps.props.options,
          ...(newOfferId && { marketplaceOfferIds: newOfferId }),
        },
      },
      id: newId,
      parentId: get(componentToCopy, 'parentId', PAGE_ROOT),
    };

    newComponent = updateMarketplaceOffers(newOfferId, newComponent);

    // Add the component
    dispatch(
      addComponent(
        newComponent,
        1 + positionInParent(idToCopy, componentToCopy.parentId, flatOutline)
      )
    );

    // recursively copy all children of this component
    if (componentToCopy.children) {
      componentToCopy.children.forEach((childComponentId) =>
        dispatch(copyComponent(childComponentId, newId))
      );
    }
  };

export const loadComponent = (componentType) => {
  const component = componentLibrary[componentType];
  return component && component.preload
    ? component.preload()
    : Promise.resolve('ok');
};

export const insertBelow =
  (siblingId, componentDocument) => (dispatch, getState) => {
    // if there is no component to insert, this should be a no-op
    if (isEmpty(componentDocument)) {
      return Promise.resolve('ok');
    }

    const {
      builder: { flatOutline },
    } = getState();
    const id = makeId(componentDocument);
    const parentId = get(flatOutline, [siblingId, 'parentId']);

    const newComponent = {
      id,
      parentId,
      ...componentDocument,
    };

    dispatch(startLoading('newComponent'));
    return loadComponent(componentDocument.type).then(() => {
      dispatch(
        addComponent(
          newComponent,
          1 + positionInParent(siblingId, parentId, flatOutline)
        )
      );
      dispatch(doneLoading('newComponent'));
    });
  };

export const insertChild = (parentId, componentDocument) => (dispatch) => {
  // if there is no component to insert, this should be a no-op
  if (isEmpty(componentDocument)) {
    return Promise.resolve('ok');
  }

  const id = makeId(componentDocument);
  const newComponent = {
    id,
    parentId,
    ...componentDocument,
  };

  dispatch(startLoading('newComponent'));
  return loadComponent(componentDocument.type).then(() => {
    dispatch(addComponent(newComponent));
    dispatch(doneLoading('newComponent'));
  });
};

export const startSorting = (componentId) => ({
  type: constants.START_SORTING,
  componentId,
});

export const stopSorting = {
  type: constants.STOP_SORTING,
};

export const moveComponent = (movingId, siblingId) => ({
  type: constants.MOVE_COMPONENT,
  movingId,
  siblingId,
});

export const copyToClipboard = (idToCopy, parentId) => (dispatch, getState) => {
  const state = getState();
  const flatOutline = get(state, 'builder.flatOutline');
  const componentToCopy = get(flatOutline, idToCopy, {});
  const type = get(componentToCopy, 'type', null);
  if (isEmpty(componentToCopy) || type === null) return;

  const newId = makeId(componentToCopy);
  let copiedProps = omit(componentToCopy, 'children');
  copiedProps = regenerateUniqueKeys(copiedProps);

  if (copiedProps.props) {
    copiedProps.props.isCopy = true;
  }

  const newComponent = {
    ...copiedProps,
    id: newId,
    parentId: parentId || get(componentToCopy, 'parentId', PAGE_ROOT),
  };

  navigator.clipboard.writeText(JSON.stringify(newComponent)).then(() => {
    toast.success('Component copied to clipboard!');
  });
};

export const pasteFromClipboard =
  (siblingId = null) =>
  (dispatch, getState) => {
    const state = getState();
    const flatOutline = get(state, 'builder.flatOutline');

    navigator.clipboard.readText().then((data) => {
      const component = JSON.parse(data);
      if (!component.id && !component.parentId) {
        toast.error('Invalid LPC component to copy');
      }
      let newComponent = omit(component, 'children');

      newComponent = regenerateUniqueKeys(newComponent);

      if (!siblingId) {
        dispatch(
          addComponent(newComponent, Object.keys(flatOutline).length - 1)
        );
      } else {
        dispatch(insertBelow(siblingId, newComponent));
      }

      // recursively copy all children of this component
      if (component.children) {
        component.children.forEach((childComponentId) =>
          dispatch(copyComponent(childComponentId, component.id))
        );
      }

      toast.success('Component pasted from clipboard!');
    });
  };

export const openPageMap = () => ({
  type: constants.OPEN_PAGE_MAP,
});

export const togglePageMap = () => ({
  type: constants.TOGGLE_PAGE_MAP,
});

export const closePageMap = () => ({
  type: constants.CLOSE_PAGE_MAP,
});

export const updatePageMapWidth = (width) => ({
  type: constants.UPDATE_PAGE_MAP_WIDTH,
  width,
});

export const setActiveElementId = (id) => ({
  type: constants.SET_ACTIVE_ELEMENT_ID,
  payload: id,
});
